import notificationsService from '../services/notificationsService.js';
import ModalBannerConstants from '../constants/ModalBannerConstants';
import { RecordStates } from '../constants/ReduxConstants';
import eventBus from '../helpers/EventBus';
import { isInsightsOnlySession } from '../helpers/user/insightsRoleAccess';

export default class NotificationsDataModel {

  /*************************************************************
   *
   *  Initialization
   *
   *************************************************************/

  constructor(store, dataModel) {
    this.store = store;
    this.dataModel = dataModel;
    this.fetchNotifications = this.fetchNotifications.bind(this);
  }

  get stateTree() {
    return this.store.getState();
  }

  fetchNotifications(callBack) {
    // GET /notifications is denied to insights-only users, and their header renders no notification
    // UI. Left unguarded the 403 reaches displayHTTPError below and pops an error toast on the
    // Insights dashboard on every login.
    if (isInsightsOnlySession()) {
      if (callBack) {
        callBack();
      }
      return;
    }
    let semaphore = this.dataModel.setSemaphore({ name: 'fetchNotifications' });
    if (semaphore) {
      notificationsService.getNotifications().then(notifications => {
        this.dataModel.clearSemaphore(semaphore);
        if (notifications.error) {
          this.dataModel.displayHTTPError('Getting notifications', notifications.error, false);
        }
        else {
          this.dataModel.catastrophicError = null;
          this.notifications = notifications ? notifications : [];
          if (notifications || notifications.length == 0) {
            const hasUnread = notifications.filter(notification => {
              return !notification.read_at;
            });
            this.dataModel.mobileBridge.fireNativeEvent('hybrid.notifications', { data: { has_unread: (hasUnread.length > 0) } });
            if (hasUnread.length > 0) {
              // All of Closeout, Progress Report, and Template use CLOSEOUT_NOTIFICATION_BANNER - only show one to avoid overwriting.
              // Priority: Closeout > Progress Report > Template version ready.
              const newestCloseoutNotification = hasUnread.find((notification) => notification.notifiable_type == 'Closeout');
              const newestProgressReportNotification = hasUnread.find((notification) =>
                notification.notifiable_type == 'ProgressReport' && notification.project_id > 0);
              const newestTemplateVersionReadyNotification = hasUnread.find((notification) =>
                notification.notifiable_type == 'ProjectTemplate' &&
                (notification.name == 'New Template Version Ready' || notification.sub_type == 'template_version_ready'));
              const closeoutBannerNotification = (newestCloseoutNotification && !newestCloseoutNotification.banner_displayed)
                ? newestCloseoutNotification
                : (newestProgressReportNotification && !newestProgressReportNotification.banner_displayed)
                  ? newestProgressReportNotification
                  : (newestTemplateVersionReadyNotification && !newestTemplateVersionReadyNotification.banner_displayed)
                    ? newestTemplateVersionReadyNotification
                    : null;
              if (closeoutBannerNotification) {
                if (closeoutBannerNotification.notifiable_type == 'Closeout') {
                  this.showCloseoutBanner(closeoutBannerNotification);
                } else if (closeoutBannerNotification.notifiable_type == 'ProgressReport') {
                  this.showProgressReportBanner(closeoutBannerNotification);
                } else {
                  this.showTemplateVersionReadyBanner(closeoutBannerNotification);
                }
                this.markBannerDisplayed(closeoutBannerNotification);
              }
              // Check if publish/migrate notification (exclude "New Template Version Ready" so we don't refetch everything)
              const newestTemplateNotification = hasUnread.find((notification) =>
                notification.notifiable_type == 'ProjectTemplate' &&
                notification.name != 'New Template Version Ready' &&
                notification.sub_type != 'template_version_ready'
              );
              if (newestTemplateNotification) {
                // NOTE: mark cached data as needing sync so projects/tasks/cc are refetched when needed
                Object.keys(this.dataModel.projectsDAO.store.getState().projects || {}).forEach(projectId => {
                  const project = this.dataModel.projectsDAO.findItem(projectId);
                  if (project) {
                    project._recordState = RecordStates.NEEDS_ATTRIBUTE_SYNC;
                    this.dataModel.projectsDAO.setItemInStore(project);
                  }
                });
                this.dataModel.collectionsDAO.markCollectionNeedsSync();
              }
              if (newestTemplateNotification && !newestTemplateNotification.banner_displayed) {
                // only show banner once for each notification
                this.showMigratedBanner(newestTemplateNotification);
                this.markBannerDisplayed(newestTemplateNotification);
              }
              const newestZipCloseoutNotification = hasUnread.find((notification) => notification.notifiable_type == 'ZipCloseout');
              if (newestZipCloseoutNotification && !newestZipCloseoutNotification.banner_displayed) {
                // only show banner once for each notification
                this.showZipCloseoutBanner(newestZipCloseoutNotification);
                this.markBannerDisplayed(newestZipCloseoutNotification);
              }
              const newestKmzCloseoutNotification = hasUnread.find((notification) => (notification.notifiable_type == 'ProjectGroup' ||
                notification.notifiable_type == 'Company' || notification.notifiable_type == 'Project'));
              if (newestKmzCloseoutNotification && !newestKmzCloseoutNotification.banner_displayed) {
                // only show banner once for each notification
                this.showKmzCloseoutBanner(newestKmzCloseoutNotification);
                this.markBannerDisplayed(newestKmzCloseoutNotification);
              }
            }
          }
          if (callBack) {
            callBack();
          }
        }
      });
    }
  }

  fetchAllNotifications(callBack) {
    let semaphore = this.dataModel.setSemaphore({ name: 'fetchAllNotifications' });
    if (semaphore) {
      notificationsService.getAllNotifications().then(notifications => {
        this.dataModel.clearSemaphore(semaphore);
        if (notifications.error) {
          this.dataModel.displayHTTPError('Getting notifications', notifications.error, false);
        }
        else {
          this.dataModel.catastrophicError = null;
          this.allNotifications = notifications ? notifications : [];
          if (callBack) {
            callBack();
          }
        }
      });
    }
  }

  get notifications() {
    if (!this.stateTree.notifications.notifications) {
      this.fetchNotifications();
      return null;
    }
    else {
      return this.stateTree.notifications.notifications;
    }
  }

  set notifications(notifications) {
    if (notifications) {
      for (let i = 0; i < notifications.length; i++) {
        this.notification = notifications[i];
      }
    }
    this.store.dispatch({
      type: 'SET_NOTIFICATIONS',
      notifications: notifications,
    });
  }

  get allNotifications() {
    if (!this.stateTree.notifications.allNotifications) {
      this.fetchAllNotifications();
      return null;
    }
    else {
      return this.stateTree.notifications.allNotifications;
    }
  }

  set allNotifications(notifications) {
    if (notifications) {
      for (let i = 0; i < notifications.length; i++) {
        this.notification = notifications[i];
      }
    }
    this.store.dispatch({
      type: 'SET_ALL_NOTIFICATIONS',
      allNotifications: notifications,
    });
  }

  getNotification(id) {
    if (this.stateTree.notifications['notification_' + id]) {
      return this.stateTree.notifications['notification_' + id].notification;
    }
    else {
      return null;
    }
  }

  get currentNotification() {
    return this.stateTree.notifications.currentNotification;  
  }

  set currentNotification(notification) {
    this.store.dispatch({
      type: 'SET_CURRENT_NOTIFICATION',
      currentNotification: notification,
    });
  }

  set newNotification(notification) {
    if (!this.stateTree.notifications.notifications) {
      this.stateTree.notifications.notifications = [];
    }

    const newNotifications = this.stateTree.notifications.notifications.filter(notif => {
      return notif.id != notification.id;
    });

    newNotifications.splice(0, 0, notification);
    this.notifications = newNotifications;

    if (notification.notifiable_type == 'Closeout') {
      this.showCloseoutBanner(notification);
      this.markBannerDisplayed(notification);
    }
    else if (notification.notifiable_type == 'ProjectTemplate' &&
      (notification.name == 'New Template Version Ready' || notification.sub_type == 'template_version_ready')) {
      this.showTemplateVersionReadyBanner(notification);
      this.markBannerDisplayed(notification);
    }
    else if (notification.notifiable_type == 'ProjectTemplate') {
      this.showMigratedBanner(notification);
      this.markBannerDisplayed(notification);
    }
    else if (notification.notifiable_type == 'ZipCloseout') {
      this.showZipCloseoutBanner(notification);
      this.markBannerDisplayed(notification);
    }
    else if (notification.notifiable_type == 'ProgressReport' && notification.project_id > 0) {
      this.showProgressReportBanner(notification);
      this.markBannerDisplayed(notification);
    }
  }

  doMark(markList, service, callBack) {
    const notification = markList[0];
    markList.splice(0, 1);
    const self = this;
    service(notification).then(newNotification => {
      if (markList.length > 0) {
        self.doMark(markList, service, callBack);
      }
      else {
        callBack();
      }
    });
  }

  markAllRead(callBack) {
    return notificationsService.markAllRead().then(notifications => {
      if (notifications.length) {
        this.notifications = notifications;
      }
      if (callBack) {
        callBack(notifications);
      }
      return notifications;
    });
  }

  markBannerDisplayed(notification, callBack) {
    this.doMark([notification], notificationsService.bannerDisplayed, () => {
      this.fetchNotifications(() => {
        if (callBack) {
          callBack();
        }
      });
    });
  }

  markActedOn(notification, callBack) {
    let markList = [];
    markList.push(notification);
    this.doMark(markList, notificationsService.actedOn, () => {
      this.fetchNotifications(() => {
        if (callBack) {
          callBack();
        }
      });
    });
  }

  markAllActedOn() { 
    return notificationsService.markAllActedOn().then(response => {
      this.notifications = [];
      return response;
    });
  }

  _getProjectNameForBanner(notification) {
    if (notification.project_id && this.dataModel.projectsDAO) {
      const project = this.dataModel.projectsDAO.findItem(notification.project_id);
      if (project && project.name) return project.name;
    }
    return notification.project_name || notification.name || 'Project';
  }

  showCloseoutBanner(notification) {
    this.dataModel.showGlobalBanner(ModalBannerConstants.CLOSEOUT_NOTIFICATION_BANNER, {
      closeoutableName: this._getProjectNameForBanner(notification),
      locationId: notification.location_id,
      projectId: notification.project_id,
      closeoutId: notification.notifiable_id,
      isMobile: false,
      clientData: notification.client_data,
    });
  }

  showMigratedBanner(notification) {
    this.dataModel.showGlobalBanner(ModalBannerConstants.PROJECTS_MIGRATED_BANNER, {
      text: notification.notes,
    });
    // The background publish/migrate job has finished.
    // 1) Refresh the published template in the store so navigating to that
    //    version later shows the correct button (publishing=false /
    //    published_at) even when no header is mounted for it right now.
    // 2) Tell any mounted ProjectTemplateHeader to drop a "PUBLISHING..." state.
    // Fires from both the live ActionCable path and the fetch-on-load path.
    if (notification.notifiable_id != null) {
      this.dataModel.projectTemplatesDAO
        .getProjectTemplate(notification.notifiable_id, { force: true })
        .catch(() => { /* ignore */ });
    }
    // Migration touched project + completion-criteria data. Invalidate the caches
    // so navigating into a migrated project refetches. Reload used to guarantee
    // this; now that we don't reload, do it explicitly (idempotent — the
    // fetch-on-load path marks the same records).
    Object.keys(this.dataModel.projectsDAO.store.getState().projects || {}).forEach(projectId => {
      const project = this.dataModel.projectsDAO.findItem(projectId);
      if (project) {
        project._recordState = RecordStates.NEEDS_ATTRIBUTE_SYNC;
        this.dataModel.projectsDAO.setItemInStore(project);
      }
    });
    this.dataModel.collectionsDAO.markCollectionNeedsSync();
    eventBus.emit('projectTemplatePublished', { projectTemplateId: notification.notifiable_id });
  }

  showZipCloseoutBanner(notification) {
    this.dataModel.showGlobalBanner(ModalBannerConstants.ZIP_DOWNLOAD_NOTIFICATION_BANNER, {
      zipFileId: notification.notifiable_id,
    });
  }

  showKmzCloseoutBanner(notification) {
    this.dataModel.showGlobalBanner(ModalBannerConstants.KMZ_DOWNLOAD_NOTIFICATION_BANNER, {
      attachment_id: notification.notes,
      kmz_id: notification.notifiable_id,
      kmz_type: notification.notifiable_type,
    });
  }

  showProgressReportBanner(notification) {
    this.dataModel.showGlobalBanner(ModalBannerConstants.CLOSEOUT_NOTIFICATION_BANNER, {
      closeoutableName: this._getProjectNameForBanner(notification),
      projectId: notification.project_id,
      isMobile: false,
      documentType: 'progressReport',
    });
  }

  showTemplateVersionReadyBanner(notification) {
    this.dataModel.showGlobalBanner(ModalBannerConstants.CLOSEOUT_NOTIFICATION_BANNER, {
      closeoutableName: notification.notes || notification.name || 'Project template',
      projectTypeId: notification.project_type_id,
      projectTemplateId: notification.notifiable_id,
      isMobile: false,
      documentType: 'projectTemplate',
    });
  }

  set notification(notification) {
    if (notification.markRead) {
      notificationsService.markRead(notification.markRead).then(newNotification => {
        // if (newNotification.error) {
        //   this.dataModel.displayHTTPError("Updating a notification", newNotification.error)
        // }
        // else {
        this.dataModel.catastrophicError = null;
        if (notification.none) {
            // No op
        }
        else if (notification.collection) {
          this.fetchNotifications();
        }
        else {
          this.notification = newNotification;
        }
        // }
      });
    }
    else if (notification.actedOn) {
      notificationsService.actedOn(notification.actedOn).then(newNotification => {
        // if (newNotification.error) {
        //   this.dataModel.displayHTTPError("Updating a notification", newNotification.error)
        // }
        // else {
        this.dataModel.catastrophicError = null;
        if (notification.none) {
            // No op
        }
        else if (notification.collection) {
          this.fetchNotifications();
        }
        else {
          this.notification = newNotification;
        }
        // }
      });
    }
    else {
      if (this.stateTree.notifications.allNotifications) {
        let foundIndex = -1;
        this.stateTree.notifications.allNotifications.filter((note, index) =>
          {
          if (note.id == notification.id) {
            foundIndex = index;
          }
          return true;
        });

        if (foundIndex >= 0) {
          this.stateTree.notifications.allNotifications[foundIndex] = notification;
        }
        else {
          this.stateTree.notifications.allNotifications.splice(0, 0, notification);
        }
      }
      this.store.dispatch({
        type: 'SET_NOTIFICATION',
        notification,
      });
    }
  }
} 



// WEBPACK FOOTER //
// ./src/models/NotificationsDataModel.js